import * as React from "react";
import { Navigate, RouteObject } from "react-router";
import { ProtectedRoute } from "modules/common/components/routing/ProtectedRoute";
import { routes } from "modules/common/routes";
import { buildRoutes as sessionRoutes } from "modules/session/routes";
import { buildRoutes as dictionariesRoutes } from "modules/dictionaries/routes";
import { buildRoutes as agentsRoutes } from "modules/agents/routes";
import { buildRoutes as ordersRoutes } from "modules/orders-manage/routes";
import { buildRoutes as spendingsRoutes } from "modules/spending/routes";
import { buildRoutes as expensesRoutes } from "modules/expenses/routes";
import { buildRoutes as technicalRoutes } from "modules/technical/routes";
import { buildRoutes as orderMailsRoutes } from "modules/order-mails/routes";
import { buildRoutes as orderConfirmsRoutes } from "modules/order-confirms/routes";
import { buildRoutes as orderQuestionsRoutes } from "modules/order-questions/routes";
import { buildRoutes as schedulesRoutes } from "modules/schedules/routes";
import { buildRoutes as reportsRoutes } from "modules/reports/routes";

import { ApplicationStoreType } from "./models/store";
import { trimEnd } from "modules/common/services/strings";
import MainAppPage from "modules/main/components/MainAppPage";
import { BotRegistration } from "modules/external/bot/components/BotRegistration";
import { UiKitPage } from "./components/ui-kit/UiKitPage";

/**
 * Полное дерево маршрутов приложения.
 *
 * В react-router 5 каждый модуль отдавал свой набор `<Route>`, и все они
 * рендерились параллельно — совпадало обычно ровно одно. В шестой версии
 * маршруты описываются объектами, а `useRoutes` выбирает один, самый точный.
 * Поэтому пути, которые раньше пересекались (внутри «Справочников»), собраны
 * во вложенные маршруты — см. `modules/dictionaries/routes`.
 *
 * Хвост `/*` у пути означает то же, что отсутствие `exact` в пятой версии:
 * маршрут подходит и для вложенных адресов.
 */
export const buildRoutes = (store: ApplicationStoreType): RouteObject[] => {
  const loggedIn = store.session.loggedIn;
  const baseUrl = trimEnd("/", store.transport.defaults.baseURL || "");

  return [
    ...sessionRoutes({
      tokens: store.tokens,
      session: store.session,
      access: store.access,
      settings: store.settings,
      baseUrl,
    }),
    ...dictionariesRoutes({ baseUrl, store }),
    ...agentsRoutes({ baseUrl, outsourcers: store.outsourcers, session: store.session }),
    ...ordersRoutes({ baseUrl, loggedIn, store }),
    ...orderMailsRoutes({ loggedIn, store: store.orderMails, session: store.session }),
    ...orderQuestionsRoutes({ loggedIn, store: store.orderQuestions, session: store.session }),
    ...schedulesRoutes({
      loggedIn,
      session: store.session,
      projectPlan: store.projectPlan,
      workloadPlan: store.workloadPlan,
      timesheet: store.timesheet,
      unit: store.unit,
      schedule: store.schedule,
    }),
    ...orderConfirmsRoutes({ loggedIn, store: store.orderConfirms, session: store.session }),
    ...spendingsRoutes({
      loggedIn,
      timesheet: store.timesheet,
      unit: store.unit,
      schedule: store.schedule,
      workloadPlan: store.workloadPlan,
      session: store.session,
      employee: store.employee,
      materials: store.inventoryItems,
      onec: store.onec,
      bankDetails: store.bankDetails,
      portfolios: store.projectPortfolios,
    }),
    ...expensesRoutes({
      loggedIn,
      requestsStore: store.financeRequests,
      importStore: store.importSpendings,
      spendingsStore: store.spendingList,
      overheadStore: store.overheadSpendings,
      session: store.session,
    }),
    ...reportsRoutes({
      loggedIn,
      session: store.session,
      reports: store.reports,
      orders: store.shortOrders,
      departments: store.departments,
      portfolios: store.projectPortfolios.dictionary,
    }),
    ...technicalRoutes({
      loggedIn,
      session: store.session,
      manuals: store.knowledgeBaseManuals,
      standards: store.knowledgeBaseStandards,
      templates: store.knowledgeBaseTemplates,
      organisations: store.knowledgeBaseOrganisations,
      orders: store.knowledgeBaseOrders,
    }),
    {
      path: routes.main.ui,
      element: (
        <ProtectedRoute loggedIn={loggedIn}>
          <UiKitPage />
        </ProtectedRoute>
      ),
    },
    {
      path: routes.main.bot(),
      element: (
        <ProtectedRoute loggedIn={loggedIn}>
          <BotRegistration store={store.session} />
        </ProtectedRoute>
      ),
    },
    // адреса объектов в уведомлениях (подпись, событие календаря, запрос): своих страниц у них нет,
    // окна открываются на Главной (specs/006-pwa-notifications, contracts/routes.md)
    ...["/signatures/:id", "/calendar/:id", "/requests/:id"].map((path) => ({
      path,
      element: <Navigate to={routes.main.path} replace />,
    })),
    {
      path: routes.main.path,
      element: (
        <ProtectedRoute loggedIn={loggedIn}>
          <MainAppPage root={store} baseUrl={baseUrl} />
        </ProtectedRoute>
      ),
    },
  ];
};
